import { FormControl, FormHelperText } from '@mui/material';
import PlacesAutocomplete, { geocodeByAddress } from 'react-google-places-autocomplete';
import { Controller } from 'react-hook-form';
import DabaduTooltip from 'sharedComponents/toolTip';
import styled from 'styled-components';
import { AddressComponent, FormInputProps } from './InputInterface';

const AutoCompleteFormControl = styled(FormControl)`
  &.MuiFormControl-root {
    width: 100%;
    [class$='-control'] {
      border-color: ${(props) => props.theme.colors.inputBorder};
      box-shadow: none;
      padding: 10px;
      @media (max-width: 1650px) {
        padding: 5px 7px;
      }
    }
  }
  .btn-clear {
    border: none;
    position: absolute;
    right: 55px;
    top: 48px;
    width: 25px;
    height: 25px;
    padding: 0;
    background-color: rgba(0, 0, 0, 0.04);
    border-radius: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:focus {
      border: none;
      outline: none;
    }
    @media (max-width: 1650px) {
      top: 38px;
    }
    svg {
      width: 15px;
    }
  }
`;

export const AutoCompleteAddress = ({
  name,
  control,
  label,
  placeholder,
  rules,
  hint,
  triggerValidationForField,
  setValue,
}: FormInputProps) => {
  // const { setValue } = useFormContext();

  const getAddressComponent = (results: AddressComponent[], type: string, short_long_name: string): string | null => {
    const component = results.find((comp) => comp.types.includes(type));
    return component ? component[short_long_name] : null;
  };

  const handleSelect = async (value: any) => {
    // lookupStreetNumbe,
    // lookupStreetName,
    // city,
    // region,
    // province,
    // country,
    // postalCode,
    // selectedAddress,
    // postal,
    // unit,
    // code

    const results = await geocodeByAddress(value.label);
    // const latLng = await getLatLng(results[0]);
    console.log('Selected Address:', value);
    console.log('Selected Address results:', results);

    const streetNumber: string | null = getAddressComponent(
      results[0].address_components,
      'street_number',
      'long_name'
    );

    setValue(`${name}_lookupStreetNumber`, streetNumber);
    const route: string | null = getAddressComponent(results[0].address_components, 'route', 'long_name');

    const locality: string | null =
      getAddressComponent(results[0].address_components, 'locality', 'long_name') ||
      getAddressComponent(results[0].address_components, 'political', 'long_name');
    setValue(`${name}_city`, locality);

    const administrativeAreaLevel1: string | null = getAddressComponent(
      results[0].address_components,
      'administrative_area_level_1',
      'short_name'
    );
    const province: string | null = getAddressComponent(
      results[0].address_components,
      'administrative_area_level_1',
      'long_name'
    );

    setValue(`${name}_region`, administrativeAreaLevel1);
    triggerValidationForField('region');

    const subpremise: string | null = getAddressComponent(results[0].address_components, 'subpremise', 'long_name');
    setValue(`${name}_unit`, subpremise);

    setValue(`${name}_code`, administrativeAreaLevel1);
    setValue(`${name}_province`, administrativeAreaLevel1);
    const country: string | null = getAddressComponent(results[0].address_components, 'country', 'short_name');
    const countryLongName: string | null = getAddressComponent(results[0].address_components, 'country', 'long_name');
    setValue(`${name}_country`, country);
    const postalCode: string | null = getAddressComponent(results[0].address_components, 'postal_code', 'short_name');
    setValue(`${name}_postalCode`, postalCode);

    const selectedAddress = `${streetNumber ? streetNumber + ', ' : ''}${route ? route + ', ' : ''}${
      locality ? locality + ', ' : ''
    }${province ? province + ', ' : ''}${countryLongName ? countryLongName + ', ' : ''}${postalCode ? postalCode : ''}`;

    setValue(`${name}_selectedAddress`, selectedAddress);
    setValue(`${name}_lookupStreetName`, route);
    setValue(`${name}_postal`, postalCode);
  };

  const clearAddressField = () => {
    setValue(name, null);
    setValue(`${name}_address`, null);
    setValue(`${name}_unit`, '');
    setValue(`${name}_postal`, '');
    setValue(`${name}_city`, '');
    setValue(`${name}_region`, '');
    setValue(`${name}_province`, '');
    setValue(`${name}_country`, '');
    setValue(`${name}_code`, '');
    setValue(`${name}_selectedAddress`, '');
    setValue(`${name}_postalCode`, '');
  };

  return (
    <AutoCompleteFormControl required={Boolean(rules)} error={Boolean(rules)}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, formState: { errors } }) => (
          <>
            {label && (
              <label className='control-label'>
                {label}
                {hint && (
                  <DabaduTooltip title={hint} placement='top-end' className='tooltip-wrapper'>
                    <span>
                      <span className='tooltip-wrapper__text'>i</span>
                    </span>
                  </DabaduTooltip>
                )}
              </label>
            )}

            <PlacesAutocomplete
              apiKey={import.meta.env.VITE_GOOGLE_MAP_API_KEY}
              debounce={300}
              selectProps={{
                placeholder: placeholder,
                value: field.value,
                onChange: (val) => {
                  field.onChange(val);
                  handleSelect(val);
                  triggerValidationForField(name);
                },
                styles: {
                  valueContainer: (base) => ({
                    ...base,
                    paddingRight: '40px', // Add your desired padding-right here
                  }),
                },
              }}
            />
            <button type='button' className='btn btn-clear' onClick={clearAddressField}>
              <svg focusable='false' aria-hidden='true' viewBox='0 0 24 24' data-testid='ClearIcon'>
                <path d='M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'></path>
              </svg>
            </button>
            {errors?.[name] && <FormHelperText>{errors?.[name]?.message}</FormHelperText>}
          </>
        )}
      />
    </AutoCompleteFormControl>
  );
};
